import * as XLSX from 'xlsx';
import { FleetPlateInput } from '../models/fleet.model';
import { US_STATES } from '../../shared/constants/us-states.constant';

const US_STATE_CODES = new Set(US_STATES.map(state => state.code));
const PLATE_STATE_SUFFIX = /^(.+)-([A-Za-z]{2})$/;

/**
 * Split a `PLATE-ST` entry (e.g. `1X25FG4-CA`) into plate and state. The suffix is only
 * treated as a state when it is a US state code; otherwise the whole value is the plate
 * and the state is left for the server to fill.
 */
export function parsePlateWithState(raw: string): FleetPlateInput {
  const value = raw.trim();
  const match = PLATE_STATE_SUFFIX.exec(value);
  const state = match?.[2].toUpperCase();
  if (match && state && US_STATE_CODES.has(state)) {
    return { license_plate: match[1].trim(), state };
  }
  return { license_plate: value, state: null };
}

/** Downloadable import template. State is optional; a blank one is filled server-side. */
export const FLEET_PLATE_TEMPLATE_CSV = 'License Plate,State\n8FGH227,CA\nJGT828G,TX\n7ABC123,\n';

const PLATE_HEADERS = ['license plate', 'plate number'];
const STATE_HEADERS = ['state', 'state code'];

const normalizeHeader = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const cellText = (value: unknown): string => String(value ?? '').trim();

/**
 * Read the first sheet of a CSV or XLSX file into plate rows.
 *
 * Both formats go through SheetJS so quoted CSV fields work, and `raw: true` keeps CSV
 * cells as text (a plate like `0123` keeps its leading zero). Header matching is
 * case-insensitive: plate column `License Plate` or `Plate Number` (required), state
 * column `State` or `State Code` (optional). State is passed through uppercased; the
 * server validates it and reports rows with a non-US code as invalid. When the State cell
 * is blank, a `PLATE-ST` plate (e.g. `1X25FG4-CA`) supplies the state.
 *
 * @throws Error with a user-facing message when the file has no plate column or no plates
 */
export function readFleetPlateSheet(data: ArrayBuffer): FleetPlateInput[] {
  const workbook = XLSX.read(new Uint8Array(data), { type: 'array', raw: true });
  const [firstSheetName] = workbook.SheetNames;
  const rows = firstSheetName
    ? XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[firstSheetName], {
        header: 1,
        raw: false,
        defval: '',
      })
    : [];

  if (rows.length < 2) {
    throw new Error('File must contain a header row and at least one license plate');
  }

  const headers = rows[0].map(normalizeHeader);
  const plateIndex = headers.findIndex(h => PLATE_HEADERS.includes(h));
  const stateIndex = headers.findIndex(h => STATE_HEADERS.includes(h));

  if (plateIndex === -1) {
    throw new Error('File must contain a "License Plate" column');
  }

  const plates = rows
    .slice(1)
    .map(row => {
      const state = stateIndex === -1 ? '' : cellText(row[stateIndex]).toUpperCase();
      // A blank State cell still accepts the `PLATE-ST` form in the plate column.
      return state
        ? { license_plate: cellText(row[plateIndex]), state }
        : parsePlateWithState(cellText(row[plateIndex]));
    })
    .filter(plate => plate.license_plate.length > 0);

  if (plates.length === 0) {
    throw new Error('No license plates found in the file');
  }

  return plates;
}
